import { CustomerType } from '../../contacts/enums/customerType'
import { getIsAddressExemptionAvailable } from '../../contacts/utils/getIsAddressExemptionAvailable'
import { InvoiceComplianceMode } from '../enums/invoiceComplianceMode'
import { hasValidVatNumber } from './hasValidVatNumber'

interface GetCustomerMissingFieldsOptions {
  city: string | null | undefined
  complianceMode: InvoiceComplianceMode
  country: string | null | undefined
  eInvoiceAddress: string | undefined
  isAddressExempt?: boolean
  isIndividual: boolean
  isVatExempted: boolean | undefined
  street: string | null | undefined
  vatNumber: string | null | undefined
  zipCode: string | null | undefined
}

interface GetCustomerMissingFieldsResult {
  isMissingAddress: boolean
  isMissingCity: boolean
  isMissingEInvoiceAddress: boolean
  isMissingStreet: boolean
  isMissingTaxInfo: boolean
  isMissingVatNumber: boolean
  isMissingZipCode: boolean
  isMissingDetails: boolean
}

export const getCustomerMissingFields = ({
  city,
  complianceMode,
  country,
  eInvoiceAddress,
  isAddressExempt = false,
  isIndividual,
  isVatExempted,
  street,
  vatNumber,
  zipCode,
}: GetCustomerMissingFieldsOptions): GetCustomerMissingFieldsResult => {
  const isAddressExemptionAvailable = getIsAddressExemptionAvailable({
    customerCountry: country,
    customerType: isIndividual ? CustomerType.Individual : CustomerType.Company,
    isFrenchComplianceMode: complianceMode === InvoiceComplianceMode.French,
  })
  const isAddressRequired = !(isAddressExemptionAvailable && isAddressExempt)
  const isMissingCity = isAddressRequired && !city
  const isMissingZipCode = isAddressRequired && !zipCode
  const isMissingEInvoiceAddress = !eInvoiceAddress
  const isMissingStreet = isAddressRequired && !street

  const isMissingShortAddress = isMissingCity || isMissingZipCode
  const isMissingFullAddress = isMissingShortAddress || isMissingStreet

  switch (complianceMode) {
    case InvoiceComplianceMode.French: {
      // Format is checked here because this flag controls CustomerTaxInfoSection visibility - without it, a customer
      // with an outdated invalid VAT would get isMissingTaxInfo: false and never see the section to correct it.
      // This getVatNoRegex validation check has to match the one for vatNumber in getFrenchCustomerFormValidationSchema.
      const isVatNumberValid = hasValidVatNumber(vatNumber, country)
      const isVatNumberRequired = !isVatExempted
      const isMissingVatNumber = !isIndividual && isVatNumberRequired && !isVatNumberValid

      return {
        isMissingAddress: isMissingFullAddress,
        isMissingCity,
        isMissingEInvoiceAddress,
        isMissingStreet,
        isMissingTaxInfo: isMissingVatNumber,
        isMissingVatNumber,
        isMissingZipCode,
        isMissingDetails: isMissingFullAddress || isMissingVatNumber,
      }
    }
    case InvoiceComplianceMode.Default: {
      return {
        isMissingAddress: isMissingShortAddress,
        isMissingCity,
        isMissingEInvoiceAddress,
        isMissingStreet: false,
        isMissingTaxInfo: false,
        isMissingVatNumber: false,
        isMissingZipCode,
        isMissingDetails: isMissingShortAddress,
      }
    }
  }
}
